import { useState, useEffect } from "react";
import { supabase, type NewsletterSubscriber } from "@/lib/supabase";

export default function NewsletterManager() {
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { fetchSubscribers(); }, []);

  const fetchSubscribers = async () => {
    setIsLoading(true);
    const { data } = await supabase.from("newsletter_subscribers").select("*").order("created_at", { ascending: false });
    setSubscribers(data || []);
    setIsLoading(false);
  };

  const filtered = subscribers.filter((s) => s.email.toLowerCase().includes(search.toLowerCase()));

  const handleDelete = async (id: string) => {
    if (!confirm("Remove this subscriber?")) return;
    const { error } = await supabase.from("newsletter_subscribers").delete().eq("id", id);
    if (!error) await fetchSubscribers();
  };

  const handleExport = () => {
    const csv = subscribers.map((s) => s.email).join("\n");
    const blob = new Blob(["email\n" + csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "newsletter-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-10 bg-gray-100 rounded w-full max-w-md" />
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="h-16 bg-gray-100 rounded-xl" />))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl text-primary-900">Newsletter Subscribers</h1>
        <button onClick={handleExport} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-900 hover:bg-primary-950 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap">
          <i className="ri-download-line" /> Export CSV
        </button>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input type="text" placeholder="Search subscribers..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((sub) => (
          <div key={sub.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center justify-between hover:shadow-md transition-all">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-gold-50 text-gold-600"><i className="ri-mail-line text-sm" /></div>
              <div className="min-w-0">
                <p className="text-sm text-gray-800 truncate">{sub.email}</p>
                <p className="text-xs text-gray-400">{new Date(sub.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
              </div>
            </div>
            <button onClick={() => handleDelete(sub.id)} className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors" aria-label="Delete"><i className="ri-delete-bin-line text-sm" /></button>
          </div>
        ))}
      </div>

      {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm bg-white rounded-xl border border-gray-100">No subscribers found.</div>}
    </div>
  );
}